import isNumber from 'lodash/isNumber';
import upperFirst from 'lodash/upperFirst';
import { memo } from 'react';
import styled from 'styled-components';

import { SEVERAL_RECURRENCE } from '@wecasa/common/src/constants/cleaning';
import type { WeeklyRecurrence } from '@wecasa/common/src/types/commons';
import colors from '@wecasa/common/src/utils/colors';
import { getCurrentCountry } from '@wecasa/common/src/utils/country';
import { formatPricePerHour } from '@wecasa/common/src/utils/formatting';

import type { MultiplePrestationsDetailsCleaningProps } from '@wecasa/customer/src/components/basket/types';
import { CONFIG_BY_COUNTRY } from '@wecasa/customer/src/constants/country-config';
import { useIsITCSelectedOrSuccessfullyPaired } from '@wecasa/customer/src/hooks/use-is-itc-selected-or-successfully-paired';
import { getFormattedPrice, getFormattedPriceForMultiple, getPlatformFees } from '@wecasa/customer/src/utils/cleaning';

import { translate } from '@wecasa/i18n';

import Accordion from '@wecasa/web-ui/src/components/accordion/accordion';
import Text from '@wecasa/web-ui/src/components/base-text/variants/text';
import Separator from '@wecasa/web-ui/src/components/separator/separator';

import useBasketSection from '../../../../../../hooks/use-basket-section';
import {
  ANIMALS_PATH,
  CLEANING_OPTIONS_PATH,
  DURATION_PATH,
  FREQUENCY_PATH,
  PRESTATIONS_PATH,
  route,
} from '../../../../../../steps/steps';
import PromoCode from '../../../../../promo-code/promo-code';
import ServiceFeeIncludedText from '../../../../common/sections/prestations/service-fee-included-text';
import Category from '../common/category';
import { CesuDeductionAlert } from '../common/cesu-deduction-alert';
import AccordionSubtitlePrice from '../common/prices/accordion-subtitle-price';
import { AccordionTitlePrice } from '../common/prices/accordion-title-price';
import { type FormattedPlatformFees, PriceLines } from '../common/prices/price-lines';
import Surcharges from '../common/surcharges';
import { TaxCreditChoiceAlert } from '../common/tax-credit-choice-alert';
import MultipleReductions from './multiple-reductions';

const t = translate('basket.cleaning.services');

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 0 20px;
`;

const StyledAccordion = styled(Accordion)`
  margin-top: 20px;
`;

const MultiplePrestationsDetails = ({
  cleaningPrestation,
  cleaningPrestationOptions,
  duration,
  hasPets,
  isLoadingPrices,
  mode,
  prices,
  promotion,
  shouldShowPromoCode,
  universeReference,
  user,
  weeklyRecurrence,
  weeklyRecurrences,
}: MultiplePrestationsDetailsCleaningProps) => {
  const { isTaxCreditAvailable } = CONFIG_BY_COUNTRY[getCurrentCountry()];
  const {
    isWecasaCarePairingSuccessful,
    isInstantTaxCreditSelected,
    isITCSelectedOrSuccessfullyPaired,
    shouldShowTaxCreditPriceInfo,
  } = useIsITCSelectedOrSuccessfullyPaired({ user, isCesuEnabled: true });

  const recurrenceType =
    weeklyRecurrence && weeklyRecurrence >= 2
      ? SEVERAL_RECURRENCE
      : Object.keys(weeklyRecurrences).find((key) => weeklyRecurrences[key as WeeklyRecurrence] === weeklyRecurrence);
  const frequencyText = isNumber(weeklyRecurrence)
    ? t(`.frequency.recurrent.${recurrenceType}`, { weeklyRecurrence })
    : undefined;

  const hasPrestations = !!cleaningPrestation;
  const hasDuration = !!duration;

  const formattedPriceAfterTax = getFormattedPriceForMultiple(hasDuration, prices?.after_tax);
  const formattedTotalPrice = getFormattedPriceForMultiple(hasDuration, prices?.total);
  const formattedTaxAmount = getFormattedPrice(!!duration, prices?.tax_credit);
  const platformFees = getPlatformFees(prices);
  // The accordion preview keeps the "/session" unit, the price lines show the total only.
  const formattedFollowingSessionsPricePerSession =
    platformFees && getFormattedPriceForMultiple(hasDuration, platformFees.followingSessionsPrice);
  const formattedPlatformFees: FormattedPlatformFees | undefined = platformFees && {
    firstSessionPrice: getFormattedPrice(hasDuration, platformFees.firstSessionPrice),
    followingSessionsPrice: getFormattedPrice(hasDuration, platformFees.followingSessionsPrice),
    feeSurchargePerHour: formatPricePerHour(platformFees.feeSurchargePerHour, { precision: 0 }),
  };

  const { isOpen, isVisible } = useBasketSection({
    data: hasPrestations,
    sectionLocations: [
      route(PRESTATIONS_PATH),
      route(FREQUENCY_PATH),
      route(DURATION_PATH),
      route(CLEANING_OPTIONS_PATH),
      route(ANIMALS_PATH),
    ],
  });

  if (!isVisible) return null;

  return (
    <Container>
      <Separator />
      <StyledAccordion
        testId='prestations-prices-accordion'
        icon='basket'
        iconLabel={t('basket.services.icon_label.basket')}
        title={upperFirst(t('universes.cleaning.title'))}
        titlePreview={
          <AccordionTitlePrice
            formattedPrice={formattedTotalPrice}
            formattedFollowingSessionsPrice={formattedFollowingSessionsPricePerSession}
            isITCSelectedOrSuccessfullyPaired={isITCSelectedOrSuccessfullyPaired}
            isLoadingPrices={isLoadingPrices}
          />
        }
        subtitle={
          <AccordionSubtitlePrice
            formattedTotalPriceAfterTax={formattedPriceAfterTax}
            formattedFirstSessionPrice={formattedPlatformFees?.firstSessionPrice}
            isITCSelectedOrSuccessfullyPaired={isITCSelectedOrSuccessfullyPaired}
            isLoadingPrices={isLoadingPrices}
            shouldShowTaxCreditLines={isTaxCreditAvailable && shouldShowTaxCreditPriceInfo}
          />
        }
        subtitleVisibility='close'
        isOpen={isOpen}
      >
        {hasPrestations ? (
          <>
            <Category
              cleaningPrestation={cleaningPrestation}
              cleaningPrestationOptions={cleaningPrestationOptions}
              duration={duration}
              hasPets={hasPets}
              frequencyText={frequencyText}
            />
            <Surcharges surcharges={prices?.surcharges} />
            <MultipleReductions hasDuration={hasDuration} prices={prices} />
            <PriceLines
              formattedTaxAmount={formattedTaxAmount}
              formattedTotalPrice={formattedTotalPrice}
              formattedPriceAfterTax={formattedPriceAfterTax}
              isITCSelectedOrSuccessfullyPaired={isITCSelectedOrSuccessfullyPaired}
              isLoading={isLoadingPrices}
              formattedPlatformFees={formattedPlatformFees}
              shouldShowTaxCreditLines={isTaxCreditAvailable && shouldShowTaxCreditPriceInfo}
            />
            <ServiceFeeIncludedText />
          </>
        ) : (
          <Text.SM color={colors.ink50}>{t('.no_services')}</Text.SM>
        )}
      </StyledAccordion>
      {isTaxCreditAvailable && shouldShowTaxCreditPriceInfo && (
        <TaxCreditChoiceAlert
          isInstantTaxCreditSelected={isInstantTaxCreditSelected}
          isWecasaCarePairingSuccessful={isWecasaCarePairingSuccessful}
        />
      )}
      {mode === 'creation' && hasPrestations && shouldShowPromoCode && (
        <PromoCode
          cleaningPrestation={cleaningPrestation}
          promotion={promotion}
          totalPrice={prices?.total.price || 0}
          universeReference={universeReference}
          user={user}
        />
      )}
      <CesuDeductionAlert totalPrice={prices?.total.price} />
    </Container>
  );
};

export default memo(MultiplePrestationsDetails);
